import React, { useMemo } from 'react';
import { Receipt, ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';

const MyTransactionActivityComponent = ({ allTransactions, currentUserId, totalOwed, totalOwe, theme }) => {
  const myActivity = useMemo(() => {
    const rows = [];
    allTransactions.forEach(txn => {
      (txn.creditTransactions || []).forEach(ct => {
        const iAmPayer = String(ct.userId) === currentUserId;
        const iAmPayee = ct.toUser && String(ct.toUser.id) === currentUserId;
        if (!iAmPayer && !iAmPayee) return;
        rows.push({
          key:         `${txn.id}-${ct.id}`,
          date:        txn.createdAt,
          title:       txn.title,
          iAmPayer,
          counterpart: iAmPayer
            ? (ct.toUser ? `${ct.toUser.firstName} ${ct.toUser.lastName}`.trim() : '?')
            : `${ct.user?.firstName || ''} ${ct.user?.lastName || ''}`.trim(),
          amount:  ct.amount,
          status:  ct.status,
          type:    txn.type,
        });
      });
    });
    return rows.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [allTransactions, currentUserId]);

  return (
    <div className="space-y-3">
      {/* My Balance Strip */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`rounded-2xl p-4 border ${theme.border} ${theme.card}`}>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span className={`text-xs font-semibold uppercase tracking-wide ${theme.textSecondary}`}>
              You are owed
            </span>
          </div>
          <p className="text-2xl font-bold text-green-500">${(totalOwed || 0).toFixed(0)}</p>
        </div>
        <div className={`rounded-2xl p-4 border ${theme.border} ${theme.card}`}>
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-4 h-4 text-red-500" />
            <span className={`text-xs font-semibold uppercase tracking-wide ${theme.textSecondary}`}>
              You owe
            </span>
          </div>
          <p className="text-2xl font-bold text-red-500">${(totalOwe || 0).toFixed(0)}</p>
        </div>
      </div>

      <div className={`${theme.card} rounded-2xl border ${theme.border} overflow-hidden`}>
      <div className="px-5 py-4 flex items-center gap-2">
        <Receipt className={`w-4 h-4 ${theme.textSecondary}`} />
        <h3 className={`font-semibold ${theme.text}`}>My Activity</h3>
        {myActivity.length > 0 && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${theme.backgroundSecondary} ${theme.textSecondary}`}>
            {myActivity.length}
          </span>
        )}
      </div>

      {myActivity.length === 0 ? (
        <div className="px-5 pb-8 text-center">
          <p className={`text-sm ${theme.textSecondary}`}>No personal transactions yet.</p>
        </div>
      ) : (
        <div className={`border-t ${theme.borderSecondary}`}>
          {/* Header row */}
          <div
            className={`grid px-4 py-2 ${theme.backgroundSecondary}`}
            style={{ gridTemplateColumns: '90px 1fr 1fr 70px 70px' }}
          >
            {['Date', 'Description', 'With', 'Amount', 'Status'].map(h => (
              <p key={h} className={`text-xs font-semibold uppercase tracking-wide ${theme.textSecondary}`}>{h}</p>
            ))}
          </div>

          <div className={`divide-y ${theme.borderSecondary}`}>
            {myActivity.map(row => (
              <div
                key={row.key}
                className="grid items-center px-4 py-3 gap-2"
                style={{ gridTemplateColumns: '90px 1fr 1fr 70px 70px' }}
              >
                <p className={`text-xs ${theme.textSecondary}`}>
                  {new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                </p>

                <p className={`text-sm ${theme.text} truncate`}>{row.title}</p>

                <div className="flex items-center gap-1 text-sm min-w-0">
                  {row.iAmPayer ? (
                    <>
                      <ArrowRight className="w-3 h-3 text-red-400 flex-shrink-0" />
                      <span className={`${theme.text} truncate`}>{row.counterpart}</span>
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-3 h-3 text-green-400 flex-shrink-0 rotate-180" />
                      <span className={`${theme.text} truncate`}>{row.counterpart}</span>
                    </>
                  )}
                </div>

                <p className={`text-sm font-semibold ${row.iAmPayer ? 'text-red-500' : 'text-green-500'}`}>
                  {row.iAmPayer ? '-' : '+'}${row.amount.toFixed(0)}
                </p>

                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${
                  row.status === 'paid'
                    ? 'bg-green-500/15 text-green-500'
                    : 'bg-orange-500/15 text-orange-500'
                }`}>
                  {row.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default MyTransactionActivityComponent;
